import { createSlice } from "@reduxjs/toolkit";
import { fetchNotificationsAsync } from "../thunks/NotificationsThunk";
import { STATUS } from "./OrganizationSlice";

// payload is an open JSON blob whose internal structure varies by insightType,
// so it is typed loosely and must be read defensively at the call site.
export interface NotificationMessage {
  title?: string;
  body?: string;
  insightData?: Record<string, unknown> | null;
  [key: string]: unknown;
}

export interface NotificationPayload {
  message?: NotificationMessage;
  [key: string]: unknown;
}

export interface NotificationItem {
  insightId: string;
  ruleKey: string | null;
  ruleName: string | null;
  insightType: string | null;
  userId: string | null;
  // firstName/lastName are resolved live and may be null when the user can't be resolved.
  firstName: string | null;
  lastName: string | null;
  triggerType: string | null;
  status: string;
  channel: string | null;
  orgId: string | null;
  createdAt: string | null;
  sentAt?: string | null;
  payload?: NotificationPayload | null;
}

interface NotificationsState {
  notifications: NotificationItem[];
  status: string;
  error: string | null;
  totalItems: number;
  pages: number;
  currentPage: number;
  perPage: number;
}

const initialState: NotificationsState = {
  notifications: [],
  status: STATUS.IDLE,
  error: null,
  totalItems: 0,
  pages: 0,
  currentPage: 1,
  perPage: 20,
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    resetNotifications: (state) => {
      state.notifications = [];
      state.status = STATUS.IDLE;
      state.error = null;
      state.totalItems = 0;
      state.pages = 0;
      state.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationsAsync.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchNotificationsAsync.fulfilled, (state, action) => {
        state.status = STATUS.SUCCESS;
        state.notifications = action.payload?.data ?? [];
        state.totalItems = action.payload?.totalItems ?? 0;
        state.pages = action.payload?.pages ?? 0;
        state.currentPage = action.payload?.currentPage ?? 1;
        state.perPage = action.payload?.perPage ?? state.perPage;
      })
      .addCase(fetchNotificationsAsync.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = (action.payload as string) ?? "An unexpected error occurred";
        state.notifications = [];
      });
  },
});

export const { resetNotifications } = notificationsSlice.actions;
export default notificationsSlice.reducer;
